<!-- Рисует donut-график с легендой и tooltip для dashboard-метрик. -->
<template>
  <div class="donut-chart" ref="wrap">
    <!-- Легенда с чекбоксами -->
    <div class="donut-chart__legend">
      <BaseCheckbox
        v-for="s in slices"
        :key="'cb-' + s.key"
        :model-value="!hiddenKeys[s.key]"
        @update:modelValue="(val) => setVisible(s.key, val)"
      >
        <span class="legend-item">
          <span
            class="legend-dot"
            :style="{ background: s.color }"
          ></span>
          {{ s.label }}
        </span>
      </BaseCheckbox>
    </div>

    <div v-if="allHidden" class="donut-chart__empty">
      Все статусы скрыты — выберите хотя бы один
    </div>

    <div v-else class="donut-chart__body">
      <div class="donut-chart__svg-wrap">
        <transition name="donut-swap" mode="out-in">
          <svg
            :key="visibleSlicesKey"
            viewBox="0 0 200 200"
            class="donut-chart__svg"
            @mouseleave="onLeave"
          >
            <circle
              v-if="total === 0"
              cx="100"
              cy="100"
              r="58"
              fill="none"
              stroke="#e5e7eb"
              stroke-width="28"
            />
            <template v-else>
              <template v-for="arc in arcs" :key="arc.key">
                <!-- Единственный сегмент - рисуем кольцо через circle -->
                <circle
                  v-if="arc.isFull"
                  cx="100"
                  cy="100"
                  r="58"
                  fill="none"
                  :stroke="arc.color"
                  stroke-width="28"
                  :opacity="
                    hoveredKey
                      ? hoveredKey === arc.key
                        ? 1
                        : 0.25
                      : 0.85
                  "
                  class="donut-chart__slice"
                  @mouseenter="onEnter(arc, $event)"
                  @mouseleave="onLeave"
                />
                <!-- Обычный сегмент -->
                <path
                  v-else
                  :d="arc.d"
                  :fill="arc.color"
                  :opacity="
                    hoveredKey
                      ? hoveredKey === arc.key
                        ? 1
                        : 0.25
                      : 0.85
                  "
                  class="donut-chart__slice"
                  @mouseenter="onEnter(arc, $event)"
                  @mouseleave="onLeave"
                />
              </template>
            </template>

            <text
              x="100"
              y="93"
              text-anchor="middle"
              class="donut-chart__center-title"
            >
              {{ centerTitle || "Итого" }}
            </text>
            <text
              x="100"
              y="116"
              text-anchor="middle"
              class="donut-chart__center-value"
            >
              {{ abbreviatedTotal }}
            </text>
          </svg>
        </transition>
      </div>

      <div class="donut-chart__stats">
        <div
          v-for="s in visibleSlices"
          :key="s.key"
          class="donut-stat-row"
          :class="{
            'donut-stat-row--dimmed':
              hoveredKey && hoveredKey !== s.key,
          }"
          @mouseenter="hoveredKey = s.key"
          @mouseleave="hoveredKey = null"
        >
          <span
            class="donut-stat-dot"
            :style="{ background: s.color }"
          ></span>
          <span class="donut-stat-label">{{ s.label }}</span>
          <span class="donut-stat-meta">
            <span v-if="s.count != null" class="donut-stat-count">
              {{ s.count }} шт.
            </span>
            <span class="donut-stat-value">
              {{ formatVal(s.value) }}
            </span>
            <span class="donut-stat-pct">
              {{ calcPct(s.value) }}%
            </span>
          </span>
        </div>
      </div>
    </div>

    <transition name="fade-slide">
      <div
        v-if="tooltip.visible"
        class="donut-chart__tooltip"
        :style="{
          left: tooltip.left + 'px',
          top: tooltip.top + 'px',
        }"
      >
        <span class="donut-chart__tooltip-label">
          {{ tooltip.label }}
        </span>
        <span class="donut-chart__tooltip-row">
          <span
            class="donut-chart__tooltip-dot"
            :style="{ background: tooltip.color }"
          ></span>
          {{ tooltip.value }}
        </span>
        <span class="donut-chart__tooltip-pct">
          {{ tooltip.pct }}% от общего
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
const CX = 100;
const CY = 100;
const R = 72;
const INNER_R = 44;
const GAP_DEG = 1.5;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(startAngle, endAngle) {
  const s = polarToCartesian(CX, CY, R, startAngle);
  const e = polarToCartesian(CX, CY, R, endAngle);
  const si = polarToCartesian(CX, CY, INNER_R, startAngle);
  const ei = polarToCartesian(CX, CY, INNER_R, endAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${s.x.toFixed(3)} ${s.y.toFixed(3)}`,
    `A ${R} ${R} 0 ${large} 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`,
    `L ${ei.x.toFixed(3)} ${ei.y.toFixed(3)}`,
    `A ${INNER_R} ${INNER_R} 0 ${large} 0 ${si.x.toFixed(
      3
    )} ${si.y.toFixed(3)}`,
    "Z",
  ].join(" ");
}

export default {
  name: "DonutChart",

  props: {
    slices: {
      type: Array,
      required: true,
      // [{ key, label, color, value, count? }]
    },
    formatValue: {
      type: Function,
      default: null,
    },
    centerTitle: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      hiddenKeys: {},
      hoveredKey: null,
      tooltip: {
        visible: false,
        left: 0,
        top: 0,
        label: "",
        value: "",
        color: "",
        pct: 0,
      },
    };
  },

  computed: {
    visibleSlices() {
      return this.slices.filter((s) => !this.hiddenKeys[s.key]);
    },

    allHidden() {
      return this.visibleSlices.length === 0;
    },

    visibleSlicesKey() {
      return this.visibleSlices.map((s) => s.key).join(",");
    },

    total() {
      return this.visibleSlices.reduce(
        (sum, s) => sum + (s.value || 0),
        0
      );
    },

    abbreviatedTotal() {
      const v = this.total;
      if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "М";
      if (v >= 1_000) return (v / 1_000).toFixed(0) + "К";
      return Math.round(v).toString();
    },

    arcs() {
      if (this.total === 0) return [];
      const nonZero = this.visibleSlices.filter((s) => s.value > 0);

      if (nonZero.length === 1) {
        const s = nonZero[0];
        return [
          {
            key: s.key,
            label: s.label,
            color: s.color,
            value: s.value,
            count: s.count,
            isFull: true,
            d: null,
          },
        ];
      }

      const gap = GAP_DEG;
      let angle = 0;
      return nonZero.map((s) => {
        const sweep = (s.value / this.total) * 360;
        const startAngle = angle + gap / 2;
        const endAngle = angle + sweep - gap / 2;
        angle += sweep;
        return {
          key: s.key,
          label: s.label,
          color: s.color,
          value: s.value,
          count: s.count,
          isFull: false,
          d: arcPath(startAngle, endAngle),
        };
      });
    },
  },

  methods: {
    setVisible(key, visible) {
      this.hiddenKeys = { ...this.hiddenKeys, [key]: !visible };
    },

    formatVal(v) {
      if (this.formatValue) return this.formatValue(v);
      return Number(v).toLocaleString("ru-RU");
    },

    calcPct(v) {
      if (!this.total) return "0.0";
      return ((v / this.total) * 100).toFixed(1);
    },

    onEnter(arc, event) {
      this.hoveredKey = arc.key;
      const wrapRect = this.$refs.wrap.getBoundingClientRect();
      let left = event.clientX - wrapRect.left + 14;
      if (left + 185 > wrapRect.width)
        left = event.clientX - wrapRect.left - 195;
      this.tooltip = {
        visible: true,
        left,
        top: Math.max(0, event.clientY - wrapRect.top - 40),
        label: arc.label,
        value: this.formatVal(arc.value),
        color: arc.color,
        pct: this.calcPct(arc.value),
      };
    },

    onLeave() {
      this.hoveredKey = null;
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped>
.donut-chart {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Легенда ── */
.donut-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── Пустое состояние ── */
.donut-chart__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #aaa;
  font-size: 14px;
  font-style: italic;
  border: 1.5px dashed #e5e7eb;
  border-radius: 10px;
}

/* ── Тело ── */
.donut-chart__body {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.donut-chart__svg-wrap {
  flex-shrink: 0;
  width: 180px;
  position: relative;
}

.donut-chart__svg {
  display: block;
  width: 100%;
  height: auto;
}

.donut-chart__slice {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.donut-chart__center-title {
  font-size: 11px;
  fill: #aaa;
  font-family: "Roboto", sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.donut-chart__center-value {
  font-size: 16px;
  font-weight: 700;
  fill: var(--color-dark);
  font-family: "Roboto", sans-serif;
}

/* ── Анимация смены конфигурации ── */
.donut-swap-enter-active {
  transition: opacity 0.3s ease;
}

.donut-swap-leave-active {
  transition: opacity 0.15s ease;
}

.donut-swap-enter-from,
.donut-swap-leave-to {
  opacity: 0;
}

/* ── Статистика (правая колонка) ── */
.donut-chart__stats {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 200px;
}

.donut-stat-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: default;
  transition: opacity 0.2s ease;
}

.donut-stat-row--dimmed {
  opacity: 0.3;
}

.donut-stat-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.donut-stat-label {
  font-size: 15px;
  color: var(--color-dark);
  font-weight: 500;
  flex: 1;
  padding-bottom: 3px;
  border-bottom: 1px dotted #333;
}

.donut-stat-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.donut-stat-count {
  font-size: 15px;
  color: #aaa;
  white-space: nowrap;
  min-width: 52px;
  text-align: right;
}

.donut-stat-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-dark);
  white-space: nowrap;
  min-width: 80px;
  text-align: right;
}

.donut-stat-pct {
  font-size: 15px;
  color: #aaa;
  white-space: nowrap;
  min-width: 42px;
  text-align: right;
}

/* ── Тултип ── */
.donut-chart__tooltip {
  position: absolute;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 10;
  min-width: 165px;
}

.donut-chart__tooltip-label {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  margin-bottom: 2px;
}

.donut-chart__tooltip-row {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-dark);
}

.donut-chart__tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.donut-chart__tooltip-pct {
  font-size: 12px;
  color: #aaa;
}

@media screen and (max-width: 1024px) {
  .donut-stat-label {
    padding-bottom: 0;
    border-bottom: unset;
  }
}

/* ── Адаптив ── */
@media screen and (max-width: 767px) {
  .donut-chart__body {
    flex-direction: column;
    align-items: flex-start;
  }

  .donut-chart__svg-wrap {
    width: 160px;
    align-self: center;
  }

  .donut-stat-meta {
    gap: 8px;
  }

  .donut-stat-value {
    min-width: 70px;
  }
}
</style>
